import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */

// Repenic configurations share one private line-item property.
let repenicCartBusy = false;
const repenicRoot = () => window.Shopify?.routes?.root || '/';
const repenicGroup = (item) => item.properties?._repenic_configuration;
const repenicPart = (item) => item.properties?.Component;
const repenicMembers = (cart, group) => cart.items.filter((item) => repenicGroup(item) === group);

async function repenicRequest(path, body) {
  const response = await fetch(repenicRoot() + path, body === undefined
    ? { headers: { Accept: 'application/json' }, cache: 'no-store' }
    : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
  const result = await response.json();
  if (!response.ok || result.errors || result.status >= 400) {
    const detail = result.description || result.errors || result.message || 'Unable to update this configuration.';
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  return result;
}

// Validate the composition before changing quantities, including repeated variants in different positions.
function repenicValidate(members) {
  const plates = members.filter((item) => repenicPart(item) === 'Plate');
  if (plates.length !== 1) throw new Error('This configuration is incomplete. Remove it and build it again.');
  const gangOption = plates[0].options_with_values?.find((option) => option.name.trim().toLowerCase() === 'gangs');
  const gangs = Number(String(gangOption?.value || '').replace(/g$/i, ''));
  if (![1, 2, 3, 4].includes(gangs) || members.length !== gangs + 1 ||
      members.some((item) => item.quantity !== plates[0].quantity) ||
      Array.from({ length: gangs }, (_, i) => 'Position ' + (i + 1)).some((part) => members.filter((item) => repenicPart(item) === part).length !== 1)) {
    throw new Error('This configuration is incomplete. Remove it and build it again.');
  }
}

function repenicCheckTarget(cart, originals, group, quantity) {
  const current = repenicMembers(cart, group);
  if (quantity === 0) return current.length === 0;
  return current.length === originals.length && originals.every((original) =>
    current.some((item) => repenicPart(item) === repenicPart(original) && item.variant_id === original.variant_id && item.quantity === quantity));
}

// change.js validates each existing line against inventory. An unsuccessful increase is rolled back.
async function repenicChangeConfiguration(group, quantity) {
  if (!Number.isSafeInteger(quantity) || quantity < 0) throw new Error('Enter a whole number of sets.');
  const initial = await repenicRequest('cart.js');
  const originals = repenicMembers(initial, group);
  if (!originals.length) throw new Error('This configuration has already been removed.');
  if (quantity === 0) {
    const updates = Object.fromEntries(originals.map((item) => [item.key, 0]));
    const result = await repenicRequest('cart/update.js', { updates });
    if (!repenicCheckTarget(result, originals, group, 0)) throw new Error('Unable to remove the complete configuration. Please try again.');
    return result;
  }
  repenicValidate(originals);
  let current = initial;
  try {
    for (const original of originals) {
      const item = repenicMembers(current, group).find((entry) => repenicPart(entry) === repenicPart(original) && entry.variant_id === original.variant_id);
      if (!item) throw new Error('The configuration changed. Please refresh your cart.');
      if (item.quantity === quantity) continue;
      current = await repenicRequest('cart/change.js', { id: item.key, quantity });
      const updated = repenicMembers(current, group).find((entry) => repenicPart(entry) === repenicPart(original));
      if (updated?.quantity !== quantity) throw new Error('There is not enough stock for this number of complete sets.');
    }
    if (!repenicCheckTarget(current, originals, group, quantity)) throw new Error('The complete configuration could not be updated.');
    return current;
  } catch (error) {
    try {
      current = await repenicRequest('cart.js');
      for (const original of originals) {
        const item = repenicMembers(current, group).find((entry) => repenicPart(entry) === repenicPart(original) && entry.variant_id === original.variant_id);
        if (!item) throw new Error('Missing component');
        if (item.quantity !== original.quantity) current = await repenicRequest('cart/change.js', { id: item.key, quantity: original.quantity });
      }
      if (!repenicCheckTarget(current, originals, group, originals[0].quantity)) throw new Error('Unable to restore quantities');
    } catch {
      throw new Error('The quantities could not be restored. Remove this configuration and build it again before checkout.');
    }
    throw error;
  }
}

function repenicGuardCheckout(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const checkout = target.closest('[name="checkout"], .cart__checkout-button, a[href*="/checkout"], shopify-accelerated-checkout-cart');
  const formCheckout = event.type === 'submit' && (event.submitter?.name === 'checkout' || /\/checkout(?:\?|$)/.test(target.getAttribute('action') || ''));
  if ((checkout || formCheckout) && (repenicCartBusy || document.querySelector('[data-rb-leader][data-rb-valid="false"]'))) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}
document.addEventListener('click', repenicGuardCheckout, true);
document.addEventListener('submit', repenicGuardCheckout, true);
const repenicObserver = new MutationObserver(() => {
  const incomplete = !!document.querySelector('[data-rb-leader][data-rb-valid="false"]');
  document.documentElement.classList.toggle('rb-cart-incomplete', incomplete);
});
repenicObserver.observe(document.documentElement, { childList: true, subtree: true });

class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  #historyCartRequest = null;

  #cancelHistoryCart = () => {
    this.#historyCartRequest?.abort();
    this.#historyCartRequest = null;
  };

  #historyNavigationType() {
    return globalThis.performance?.getEntriesByType?.('navigation')[0]?.type;
  }

  #handleHistoryPageShow = (event) => {
    if (!event.persisted && this.#historyNavigationType() !== 'back_forward') return;
    this.#restoreHistoryCart();
  };

  #restoreHistoryCart = async () => {
    const sectionId = this.dataset.sectionId;
    if (!sectionId || repenicCartBusy) return;

    this.#cancelHistoryCart();
    const request = new AbortController();
    this.#historyCartRequest = request;

    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url, {
        credentials: 'same-origin',
        cache: 'no-store',
        signal: request.signal,
      });

      if (!response.ok) throw new Error('Unable to refresh cart');

      const html = await response.text();

      if (request.signal.aborted || !this.isConnected) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const wrapperId = `shopify-section-${sectionId.replace(/^shopify-section-/, '')}`;
      const nextSection = doc.getElementById(wrapperId);
      const currentSection = document.getElementById(wrapperId);
      const isDrawer = this.hasAttribute('data-drawer');

      const next = [
        ...(nextSection?.querySelectorAll('cart-items-component') || []),
      ].find(element =>
        element.dataset.sectionId === sectionId &&
        element.hasAttribute('data-drawer') === isDrawer
      );

      if (!next || !currentSection) {
        throw new Error('Cart markup not found');
      }

      const dialog = this.closest('dialog');
      const nextDialog = next.closest('dialog');

      if (isDrawer && (!dialog || !nextDialog)) {
        throw new Error('Cart dialog not found');
      }

      const count = Number(
        next.querySelector('[ref="cartItemCount"]')?.textContent?.trim() || 0
      );

      if (!Number.isInteger(count) || count < 0) {
        throw new Error('Invalid cart count');
      }

      // 补充空购物车变成有商品时需要的区块样式。
      const sourceStyle =
        nextSection.querySelector('style[data-section-stylesheet]') ||
        doc.querySelector('style[data-section-stylesheet]');

      if (sourceStyle) {
        let localStyle = currentSection.querySelector(
          'style[data-repenic-cart-history-style]'
        );

        if (!localStyle) {
          localStyle = document.createElement('style');
          localStyle.setAttribute('data-repenic-cart-history-style', '');
          currentSection.appendChild(localStyle);
        }

        localStyle.textContent = sourceStyle.textContent;
      }

      // 只更新购物车组件内部，不重绘页头、菜单或抽屉外壳。
      morph(this, next, MORPH_OPTIONS);

      // 同步外层空购物车状态，避免继续套用空购物车布局。
      if (dialog && nextDialog) {
        dialog.classList.toggle(
          'cart-drawer--empty',
          nextDialog.classList.contains('cart-drawer--empty')
        );

        const labelledBy = nextDialog.getAttribute('aria-labelledby');

        if (labelledBy === null) {
          dialog.removeAttribute('aria-labelledby');
        } else {
          dialog.setAttribute('aria-labelledby', labelledBy);
        }
      }

      this.updatedCallback();

      for (const element of this.querySelectorAll('*')) {
        if (element instanceof Component) element.updatedCallback();
      }

      this.#updateCartQuantitySelectorButtonStates();

      // 直接静默更新角标，不发送全局购物车更新事件。
      for (const icon of document.querySelectorAll('cart-icon')) {
        if (typeof icon.renderCartBubble === 'function') {
          icon.renderCartBubble(count, false, false);
        }
      }

      if (dialog?.open) {
        const summary = dialog.querySelector('.cart-drawer__summary');
        const height = dialog.getBoundingClientRect().height;

        const sticky = Boolean(
          summary &&
          height > 0 &&
          summary.getBoundingClientRect().height / height <= 0.5
        );

        dialog.setAttribute('cart-summary-sticky', String(sticky));
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[cart] History refresh failed:', error);
      }
    } finally {
      if (this.#historyCartRequest === request) {
        this.#historyCartRequest = null;
      }
    }
  };

  connectedCallback() {
    super.connectedCallback();

    window.addEventListener('pageshow', this.#handleHistoryPageShow);

    document.addEventListener(
      ThemeEvents.cartUpdate, this.#cancelHistoryCart, true
    );
    document.addEventListener(
      ThemeEvents.discountUpdate, this.#cancelHistoryCart, true
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate, this.#cancelHistoryCart, true
    );

    this.addEventListener('input', this.#cancelHistoryCart, true);

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    if (this.#historyNavigationType() === 'back_forward') {
      this.#restoreHistoryCart();
    }
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.#handleHistoryPageShow);

    document.removeEventListener(
      ThemeEvents.cartUpdate, this.#cancelHistoryCart, true
    );
    document.removeEventListener(
      ThemeEvents.discountUpdate, this.#cancelHistoryCart, true
    );
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate, this.#cancelHistoryCart, true
    );

    this.removeEventListener('input', this.#cancelHistoryCart, true);
    this.#cancelHistoryCart();

    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    if (repenicCartBusy) return;
    const rbRow = this.refs.cartItemRows[line - 1];
    if (rbRow?.dataset.rbGroup) {
      return this.updateRepenicConfiguration(rbRow.dataset.rbGroup, 0);
    }
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    if (repenicCartBusy) return;
    const rbRow = this.refs.cartItemRows[config.line - 1];
    if (rbRow?.dataset.rbGroup) {
      return this.updateRepenicConfiguration(rbRow.dataset.rbGroup, Number(config.quantity));
    }
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }


  async updateRepenicConfiguration(group, quantity) {
    if (repenicCartBusy) return;
    repenicCartBusy = true;
    document.documentElement.classList.add('rb-cart-updating');
    const sectionId = this.sectionId;
    const isDrawer = this.isDrawer;
    const sections = [...new Set(Array.from(document.querySelectorAll('cart-items-component'), (item) => item.dataset.sectionId).filter(Boolean))].slice(0, 5);
    const showMessage = (message) => {
      for (const row of document.querySelectorAll('[data-rb-leader]')) {
        if (row.dataset.rbGroup === group) {
          const output = row.querySelector('[data-rb-message]');
          if (output) output.textContent = message;
        }
      }
    };
    showMessage('');
    let message = '';
    let refreshed = false;
    try {
      await repenicChangeConfiguration(group, quantity);
    } catch (error) {
      message = error instanceof Error ? error.message : 'Unable to update this configuration.';
    }
    try {
      // No quantity changes here: obtain authoritative totals and HTML after update or rollback.
      const cart = await repenicRequest('cart/update.js', { updates: {}, sections, sections_url: window.location.pathname });
      this.#updateQuantitySelectors(cart);
      this.dispatchEvent(new CartUpdateEvent(cart, sectionId, {
        itemCount: cart.item_count, source: 'cart-items-component', sections: cart.sections,
      }));
      if (cart.sections?.[sectionId]) {
        await morphSection(sectionId, cart.sections[sectionId], { mode: isDrawer ? 'hydration' : 'full' });
      } else {
        await sectionRenderer.renderSection(sectionId, { cache: false });
      }
      this.#updateCartQuantitySelectorButtonStates();
      refreshed = true;
    } catch {
      message = 'The cart could not be refreshed. Reload the page to check the configuration before checkout.';
    } finally {
      resetShimmer(this);
      showMessage(message);
      if (refreshed) {
        repenicCartBusy = false;
        document.documentElement.classList.remove('rb-cart-updating');
      } else {
        // Keep checkout blocked when the actual server state cannot be verified.
        window.alert(message);
      }
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!Array.isArray(updatedCart.items)) return;

    const totals = new Map();
    for (const item of updatedCart.items) {
      const variantId = String(item.variant_id);
      totals.set(variantId, (totals.get(variantId) || 0) + item.quantity);
    }

    for (const selector of document.querySelectorAll('quantity-selector-component[data-variant-id]')) {
      const input = selector.querySelector('input[data-cart-quantity]');
      if (!input) continue;

      const quantity = totals.get(selector.dataset.variantId) || 0;
      input.setAttribute('data-cart-quantity', String(quantity));

      if (typeof selector.updateCartQuantity === 'function') {
        selector.updateCartQuantity();
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
